import React, { CSSProperties, useCallback, useEffect, useMemo } from 'react';
import { styled } from 'styled-components';
import { Table, TableProps } from 'antd';
import debounce from 'lodash.debounce';
import { TablePaginationConfig } from 'antd/lib';
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import { ColumnType } from 'antd/lib/table';
import { FilterComponent } from './FilterComponent';
import scrollIntoView from 'scroll-into-view';
import {
  createParamsForInfiniteScroll,
  InfiniteScrollParams,
  YColumnsType,
} from './infiniteScrollTableLib';
import { COLORS, STYLE } from '@ydistri/theme';

export const ScrollTableWrapper = styled.div<{
  $id: string;
  $xSmall?: boolean;
  $border?: boolean;
  $minHeight?: CSSProperties['minHeight'];
  $height?: CSSProperties['height'];
  $maxHeight: CSSProperties['maxHeight'];
}>`
  #${props => props.$id} .ant-table-body {
    overflow-x: hidden;
    overflow-y: scroll;
    height: ${props => props.$height ?? 'auto'};
    max-height: ${props => props.$maxHeight};
    ${props => (props.$minHeight ? `min-height: ${props.$minHeight};` : '')}
    border: ${props => (props.$border ? `1px solid ${COLORS.BORDER}` : '0')};

    ${STYLE.SCROLLBAR}
    .selected {
      background-color: ${props => props.theme.item.selected.backgroundColor};
    }

    ${p =>
      p.$xSmall
        ? `
          td.ant-table-cell:not(.ant-table-cell-scrollbar),
          th.ant-table-cell {
            padding: 0.25rem !important;
            padding-right: 0.75rem !important;
          }
        `
        : null}
    tr.green:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.green,
    div.ant-table-row.green:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.green {
      background-color: ${COLORS.TABLE_HIGHLIGHT_GREEN};
    }

    tr.blue:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.blue,
    div.ant-table-row.blue:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.blue {
      background-color: ${COLORS.TABLE_HIGHLIGHT_BLUE};
    }

    tr.orange:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.orange,
    div.ant-table-row.orange:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.orange {
      background-color: ${COLORS.TABLE_HIGHLIGHT_ORANGE};
    }

    tr.red:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.red,
    div.ant-table-row.red:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.red {
      background-color: ${COLORS.TABLE_HIGHLIGHT_RED};
    }

    tr.grey:not(.ant-table-row-selected) td,
    tr:not(.ant-table-row-selected) td.grey,
    div.ant-table-row.grey:not(.ant-table-row-selected) > div.ant-table-cell,
    div.ant-table-row:not(.ant-table-row-selected) > div.ant-table-cell.grey {
      background-color: ${COLORS.TABLE_HIGHLIGHT_GREY};
    }

    tr.green:hover td,
    tr.orange:hover td,
    tr.red:hover td,
    div.ant-table-row.green:hover > div.ant-table-cell,
    div.ant-table-row.orange:hover > div.ant-table-cell,
    div.ant-table-row.red:hover > div.ant-table-cell {
      filter: brightness(0.95);
    }
  }
`;

export const tableScroll = { y: 'auto', scrollToFirstRowOnChange: true }; //scrollToFirstRowOnChange: false

const isRowMovement = (event: React.KeyboardEvent<HTMLDivElement>) => {
  const movementKeys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'];
  return movementKeys.includes(event.key);
};

const InternalTable = <T extends object>(props: TableProps<T>): React.ReactElement => {
  return <Table<T> size="small" pagination={false} scroll={tableScroll} {...props} />;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- preserve generic props through React.memo
const MemoizedInternalTable = React.memo(InternalTable) as <T extends object>(
  props: TableProps<T>,
) => React.ReactElement;

interface InfiniteScrollTableProps<T, U extends InfiniteScrollParams> extends TableProps<T> {
  id: string;
  searchValue?: string;
  height?: CSSProperties['height'];
  minHeight?: CSSProperties['minHeight'];
  setParams: (params: U | ((prev: U) => U)) => void;
  selectedRow?: T;
  onSelectRow?: (record: T) => void;
  totalRowCount?: number;
  xSmall?: boolean /* antd table already has property Size, but "small" is still too big */;
  addBorder?: boolean;
  showHeader?: boolean;
}

export const InfiniteScrollTable = <T extends object, U extends InfiniteScrollParams>({
  id,
  searchValue,
  height,
  minHeight,
  setParams,
  onChange,
  selectedRow,
  onSelectRow,
  columns,
  totalRowCount,
  xSmall,
  addBorder,
  rowKey,
  dataSource,
  showHeader = true,
  ...rest
}: InfiniteScrollTableProps<T, U>): React.ReactElement => {
  const getRowKey = useCallback(
    (record: T) => {
      if (typeof rowKey === 'function') {
        return rowKey(record);
      }
    },
    [rowKey],
  );

  const selectedRowIndex = useMemo(() => {
    if (dataSource && selectedRow) {
      const selectedRowKey = getRowKey(selectedRow);
      return dataSource.findIndex(item => getRowKey(item) === selectedRowKey);
    } else {
      return null;
    }
  }, [getRowKey, dataSource, selectedRow]);

  const selectRowCallback = useCallback(
    (record: T) => {
      if (onSelectRow) {
        onSelectRow(record);
        const rowKey = getRowKey(record);

        if (rowKey) {
          const rowElement = document.querySelector<HTMLTableRowElement>(
            `[data-row-key="${rowKey}"]`,
          );
          if (rowElement) {
            scrollIntoView(rowElement);
          }
        }
      }
    },
    [getRowKey, onSelectRow],
  );

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isRowMovement(event)) {
        return;
      }

      event.preventDefault();

      let newRowIndex = selectedRowIndex ?? 0;

      switch (event.key) {
        case 'Home':
          newRowIndex = 0;
          break;
        case 'ArrowDown':
          newRowIndex++;
          break;
        case 'ArrowUp':
          newRowIndex--;
          break;
        case 'PageDown':
          newRowIndex += 10;
          break;
        case 'PageUp':
          newRowIndex -= 10;
          break;
        case 'End':
          newRowIndex = (dataSource ?? []).length - 1;
          break;
      }

      if (newRowIndex < 0 || newRowIndex >= (dataSource ?? []).length) {
        return;
      } else {
        const record = dataSource?.[newRowIndex];
        if (record) {
          selectRowCallback(record);
        }
      }
    },
    [selectedRowIndex, dataSource, selectRowCallback],
  );

  const getColumnSearchProps = useCallback((column: ColumnType<T>): ColumnType<T> => {
    const tmpColumn: YColumnsType<T> = column;
    const itemType = tmpColumn.apiFilterType;

    if (tmpColumn.apiFilterType !== 'options') {
      return {
        ...column,
        filterDropdown: props => (
          <FilterComponent
            columnKey={column.key?.toString() ?? ''}
            filter={props}
            displayName={
              typeof column.title === 'string' ? column.title : (column.key?.toString() ?? '')
            }
            itemType={itemType ?? 'text'}
          />
        ),
      };
    } else {
      return column;
    }
  }, []);

  const filterEnabledColumns = useMemo(() => {
    if (columns) {
      return columns.map(column => {
        //const tmpColumn = column as YColumnsType<T>;
        if ('apiFilterable' in column) {
          if (column.apiFilterable) {
            return getColumnSearchProps(column);
          }
        }
        return column;
      });
    }
  }, [columns, getColumnSearchProps]);

  const loadMore = useCallback(() => {
    const skip = dataSource?.length ?? 0;

    setParams(p => ({ ...p, skip: skip }));
  }, [dataSource?.length, setParams]);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = useCallback(
    e => {
      let shouldLoadMore = true;
      if (totalRowCount !== undefined) {
        shouldLoadMore = totalRowCount > (dataSource?.length ?? 0);
      }

      if (shouldLoadMore) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- needed
        const node = e.target as HTMLDivElement | undefined;
        if (node) {
          const percentage = (node.scrollTop / (node.scrollHeight - node.clientHeight)) * 100;
          // console.log(node.scrollTop, node.scrollHeight, node.clientHeight, percentage);
          if (percentage >= 75) {
            loadMore();
          }
        }
      }
    },
    [loadMore, dataSource?.length, totalRowCount],
  );

  // ====================== SEARCH ======================

  const searchValueChangeHandler = useMemo(
    () =>
      debounce((search: string) => {
        setParams(prevValue => {
          const newValue = {
            ...prevValue,
            skip: 0,
          };

          if (search.length > 0) {
            newValue.search = search;
          } else {
            delete newValue.search;
          }

          return newValue;
        });
      }, 500),
    [setParams],
  );

  useEffect(() => {
    if (searchValue !== undefined) {
      searchValueChangeHandler(searchValue);
    }
  }, [searchValue, searchValueChangeHandler]);

  const tableChangeHandler = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
      extra: TableCurrentDataSource<T>,
    ) => {
      setParams(previousParams => {
        return createParamsForInfiniteScroll(filters, sorter, previousParams, columns);
      });

      if (onChange) {
        onChange(pagination, filters, sorter, extra);
      }
    },
    [columns, onChange, setParams],
  );

  const internalTable = (
    <MemoizedInternalTable
      id={id}
      onChange={tableChangeHandler}
      columns={filterEnabledColumns}
      onScroll={handleScroll}
      showHeader={showHeader}
      rowKey={rowKey}
      dataSource={dataSource}
      {...rest}
    />
  );

  return (
    <ScrollTableWrapper
      tabIndex={0}
      onKeyDown={onSelectRow ? keyDownHandler : undefined}
      $height={height}
      $maxHeight={height}
      $minHeight={minHeight}
      $id={id}
      $xSmall={xSmall}
      $border={addBorder}
    >
      {internalTable}
    </ScrollTableWrapper>
  );
};
